// Ett tydligt nästa steg för hela projektet, i klarspråk.
import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PROCESS_STEPS } from "@/lib/basProcess/constants";
import { ArrowRight, CheckCircle2, Compass } from "lucide-react";

interface Props {
  projectId: string;
  onOpenLink?: (link: string) => void;
}

/** Kort, vardaglig förklaring av varför steget behövs. */
const WHY: Record<string, string> = {
  "01": "Beskriv hur ni arbetar med arbetsmiljö i projektet.",
  "05": "Byggherren utser Bas-P och Bas-U skriftligt.",
  "03": "Svara på några frågor om projektets storlek och risk.",
  "04": "Anmäl projektet till Arbetsmiljöverket om det är tillräckligt stort.",
  "10": "Gå igenom arbetsområdet: vad finns runt omkring och vad påverkar arbetet.",
  "11": "Lista riskerna i byggskedet och hur de ska hanteras.",
  "12": "Samla allt i arbetsmiljöplanen – projektets huvuddokument.",
  "13": "Kryssa i vilka av de särskilda riskerna som finns i projektet.",
  "15": "Skriv de regler som gäller för alla på arbetsplatsen.",
  "16": "Projektörerna lämnar in de risker de inte kunnat bygga bort.",
  "20": "Lämna över planeringen från Bas-P till Bas-U.",
  "21": "Följ upp på plats att åtgärderna faktiskt görs.",
  "24": "Bered de moment som är riskfyllda innan de startar.",
  "27": "Gå skyddsrond och protokollför det ni ser.",
  "28": "Rapportera tillbud och olyckor.",
  "31": "Skriv ned de risker som finns kvar när huset används.",
  "34": "Sammanfatta dokumentationen inför överlämningen.",
};

export const ProjectNextStep: React.FC<Props> = ({ projectId, onOpenLink }) => {
  const [statuses, setStatuses] = useState<Record<string, string>>({});
  const [external, setExternal] = useState<Record<string, boolean>>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const [{ data: steps }, { data: ext }] = await Promise.all([
        supabase.from("process_steps").select("doc_number, status").eq("project_id", projectId),
        supabase
          .from("external_step_completions")
          .select("doc_number, completed")
          .eq("project_id", projectId),
      ]);
      if (!active) return;
      const map: Record<string, string> = {};
      (steps || []).forEach((r: { doc_number: string; status: string }) => {
        map[r.doc_number] = r.status;
      });
      setStatuses(map);
      const extMap: Record<string, boolean> = {};
      (ext || []).forEach((r: { doc_number: string; completed: boolean | null }) => {
        extMap[r.doc_number] = !!r.completed;
      });
      setExternal(extMap);
      setLoaded(true);
    };
    load();
    return () => {
      active = false;
    };
  }, [projectId]);

  if (!loaded) return null;

  const statusOf = (doc: string, externallyManaged?: boolean) => {
    if (externallyManaged && external[doc]) return "done";
    return statuses[doc] || "not_started";
  };

  const relevant = PROCESS_STEPS.filter(
    (s) => statusOf(s.docNumber, s.externallyManaged) !== "not_applicable"
  );
  const done = relevant.filter((s) => statusOf(s.docNumber, s.externallyManaged) === "done");
  const next = relevant.find((s) => statusOf(s.docNumber, s.externallyManaged) !== "done");

  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-2 text-sm font-medium">
            {next ? (
              <Compass className="h-4 w-4 text-primary" />
            ) : (
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            )}
            {next ? "Gör det här härnäst" : "Allt i grundprocessen är klart"}
          </div>
          {next ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-base font-semibold">{next.title}</span>
                <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground">
                  Dok {next.docNumber}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {WHY[next.docNumber] || next.description}
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Fortsätt med det löpande arbetet: skyddsronder, arbetsberedningar och tillbud.
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            {done.length} av {relevant.length} steg klara
          </p>
        </div>
        {next?.appLink && onOpenLink && (
          <Button className="gap-1 sm:w-auto" onClick={() => onOpenLink(next.appLink!)}>
            Öppna <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default ProjectNextStep;
